<template>
  <BaseModal>
    <div class="login">
      <div class="login__content" @click.stop>
        <div class="login__header">
          <button v-if="page === 2" @click="backPage" class="login__back">
            <img src="/gray-arrow.svg" alt="" loading="lazy" />
          </button>

          <Logo class="login__logo" />
        </div>

        <form
          v-if="page === 1"
          @submit.prevent="getCode('call')"
          class="login__form"
        >
          <h2 class="login__title">{{ $t("login.title") }}</h2>
          <p class="login__form_text">{{ $t("login.description") }}</p>

          <VueTelInput
            v-model="phone"
            @input="filterPhone"
            id="phone"
            name="phone"
            :class="{ 'input-error': phoneError }"
            mode="international"
            country-icon-mode="svg"
            :auto-format="true"
            :enabled-flags="true"
            :input-options="{
              showDialCode: true,
              required: true,
            }"
            @blur="validatePhone"
          />
          <p v-if="phoneError" class="login__error">{{ phoneError }}</p>

          <p v-if="timer > 0" class="login_timer">
            {{ $t("verification.code") }} {{ timer }}
          </p>

          <button
            type="submit"
            :disabled="phone.length < 5 || !!phoneError"
            class="login_btn"
          >
            {{ $t("continue") }}
          </button>

          <p class="login__form_text">
            {{ $t("login.bottom") }}
          </p>
        </form>

        <form @submit.prevent="submit" v-if="page === 2" class="login__form">
          <h2 class="login__title">
            {{ $t("verification.title") }}
          </h2>
          <p class="login__form_text">
            {{ $t("verification.description") }}
          </p>

          <input
            v-model="code"
            ref="codeInput"
            type="text"
            @input="filterCode"
            id="code"
            name="code"
            pattern="[0-9]{4}"
            :placeholder="$t('verification.placeholder')"
          />
          <p v-if="codeError" class="login__error">{{ codeError }}</p>

          <p v-if="timer > 0" class="login_timer">
            {{ $t("verification.code") }} {{ timer }}
          </p>
          <a v-else class="login_get_code" @click="getCode('sms')">{{
            $t("verification.codeSms")
          }}</a>

          <button class="login_btn" :disabled="!code" type="submit">
            {{ $t("continue") }}
          </button>
          <p class="login__form_text">
            {{ $t("verification.bottom") }}
          </p>
        </form>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { ref, defineAsyncComponent } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/authStore";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useModalStore } from "@/stores/modalStore";
import { nextTick } from "vue";
import Logo from "../UI/Logo.vue";

export default {
  name: "Login",
  data() {
    return {
      modal: useModalStore(),
      currentQrImage: "",
      router: useRouter(),
      auth: useAuthStore(),
      phone: "",
      code: "",
      error: "",
      page: 1,
      phoneError: "",
      codeError: "",
      timer: ref(0),
      timerInterval: null,
    };
  },
  components: {
    VueTelInput,
    Logo,
    BaseModal: defineAsyncComponent(() => import("@/UI/Modals/BaseModal.vue")),
  },
  methods: {
    filterPhone: function () {
      this.phone = this.phone.replace(/(?!^\+)[^\d]/g, "");

      if (this.phone.length > 16) {
        this.phone = this.phone.slice(0, 16);
      }

      if (this.phoneError) this.phoneError = "";
    },
    filterCode: function () {
      this.code = this.code.replace(/[^\d]/g, "");

      if (this.code.length > 4) {
        this.code = this.code.slice(0, 4);
      }

      if (this.codeError) this.codeError = "";
    },
    countdownLogic() {
      const savedTime = localStorage.getItem("code_sent_at");

      if (!savedTime) {
        this.timer = 0;
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        return;
      }

      const startTime = Number(savedTime);
      const diff = 60 - Math.floor((Date.now() - startTime) / 1000);

      if (diff <= 0) {
        this.timer = 0;
        localStorage.removeItem("code_sent_at");
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        return;
      }

      this.timer = diff;
    },
    startTimerFromStorage() {
      const savedTime = localStorage.getItem("code_sent_at");

      if (savedTime && !this.timerInterval) {
        this.countdownLogic();
        this.timerInterval = setInterval(this.countdownLogic, 1000);
      }
    },
    startTimer() {
      const now = Date.now();
      localStorage.setItem("code_sent_at", now);

      if (!this.timerInterval) {
        this.countdownLogic();
        this.timerInterval = setInterval(this.countdownLogic, 1000);
      }
    },
    validatePhone() {
      if (!this.phone) {
        this.phoneError = this.$t("login.phoneEmpty");
        return false;
      }

      try {
        const parsed = parsePhoneNumberFromString(this.phone);
        if (!parsed || !parsed.isValid()) {
          this.phoneError = this.$t("login.incorrectPhone");
          return false;
        }
        this.phone = parsed.number;
        this.phoneError = "";
        return true;
      } catch (e) {
        this.phoneError = this.$t("login.incorrectPhone");
        return false;
      }
    },
    getCode(method = "sms") {
      if (!this.validatePhone()) return;

      if (this.timer > 0) {
        this.page = 2;
        return;
      }

      try {
        this.auth.getCode(this.phone, method);
        this.page = 2;

        this.startTimer();

        nextTick(() => {
          this.$refs.codeInput?.focus();
        });
      } catch (err) {
        this.phoneError = this.$t("login.error") + err;
        this.page = 1;
      }
    },
    backPage() {
      this.page = 1;
    },
    async submit() {
      if (!this.validatePhone()) {
        this.codeError = this.$t("login.validateError") + this.phone;
        return;
      }

      try {
        await this.auth.login(this.phone, this.code);
      } catch (err) {
        this.codeError = this.$t("login.authError") + err;
      }

      this.modal.closeAllModals();
      this.router.push({ name: "Profile" });
    },
  },
  mounted() {
    this.startTimerFromStorage();
  },
};
</script>

<style>
.login {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login__logo {
  width: 145px;
  height: 26px;
  margin: 0 auto;
}

.login__header {
  display: flex;
  width: 100%;
}

.login__title {
  margin-top: 30px;
  font-size: 20px;
}

.login__back {
  background-color: rgba(0, 0, 0, 0);
  cursor: pointer;
  border-radius: 5px;
  padding: 5px;
  margin-right: -30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login__back:hover {
  background-color: var(--hover);
}

.login__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--header-color);
  border-radius: 20px;
  padding: 24px;
  width: 352px;
  box-sizing: border-box;
}

.login__form {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login__form_text {
  color: rgba(142, 142, 147, 1);
  margin-top: 16px;
  font-size: 13px;
  font-weight: 300;
}

.login__text {
  font-size: 13px;
  font-weight: 700;
  color: #454545;
  margin: 8px 65px;
}

.language_controll {
  background: #424242;
  border-radius: 5px;
  width: 35px;
  height: 35px;
  margin-right: 10px;
  cursor: pointer;
}

.input-error input {
  border: 1px solid #c44 !important;
}

.login_form__text {
  opacity: 50%;
  font-size: 12px;
  font-weight: 300;
}

.login_timer {
  margin-top: 10px;
  opacity: 50%;
  font-size: 13px;
}

.login_get_code {
  color: rgba(251, 191, 36, 1);
  margin-top: 10px;
  font-size: 14px;
}

.login_get_code:hover {
  cursor: pointer;
  color: rgba(251, 191, 36, 0.5);
}

#phone {
  background: rgba(0, 0, 0, 0);
  border: none;
  margin-top: 24px;
}

#phone input {
  background: var(--login-input);
  padding: 12px 10px;
  border: 1px solid #2c2c2e;
  border-radius: 10px;
  color: var(--text-color);
}

.phone_input {
  display: block;
}

.vue-tel-input:focus-within {
  box-shadow: none !important;
}

.vti__dropdown {
  border-radius: 10px;
  margin-right: 10px;
  padding: 2px;
  cursor: pointer;
  border: none;
}

.vti__dropdown li {
  background-color: var(--btn-bg-color);
}

/* Стили для выпадающего списка стран */
.vti__dropdown-list {
  border: none !important;
  background-color: var(--header-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  width: 302px;
  left: 0 !important;
  right: 0 !important;
  box-sizing: border-box !important;
}

/* Стили для отдельных элементов в выпадающем списке */
.vti__dropdown-item {
  background-color: var(--header-color) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  font-size: 12px !important;
  padding: 10px 12px !important;
}

.vti__dropdown-list::-webkit-scrollbar {
  width: 6px !important;
}

.vti__dropdown-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 3px !important;
}

.vti__dropdown-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3) !important;
  border-radius: 3px !important;
}

.vti__dropdown-list::-webkit-scrollbar-thumb:hover {
  background: var(--hover) !important;
}

/* Для Firefox */
.vti__dropdown-list {
  scrollbar-width: thin !important;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1) !important;
}

.vti__dropdown-item:last-child {
  border-bottom: none !important;
}

.vti__dropdown-item:hover {
  background-color: var(--hover) !important;
}

/* Стили для выбранного элемента */
.vti__dropdown-item.highlighted {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* Убрать обводку у кнопки выбора страны */
.vti__dropdown {
  border: none !important;
  background: transparent !important;
}

.vti__dropdown:hover {
  background-color: var(--hover) !important;
}

.vti__dropdown {
  background: var(--login-input) !important;
}

#code {
  padding: 12px 10px;
  background: var(--login-input);
  color: var(--text-color);
  border: 1px solid #636366;
  border-radius: 10px;
  width: 100%;
  font-size: 15px;
  font-weight: 700;
  margin-top: 24px;
  box-sizing: border-box;
}

.login__label {
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 15px;
  text-align: left;
  margin-top: 24px;
}

.login_btn {
  padding: 10px;
  background: rgba(251, 191, 36, 1);
  border-radius: 10px;
  width: 100%;
  color: black;
  font-weight: 600;
  font-size: 16px;
  margin-top: 10px;
  transition: 0.2s all;
  cursor: pointer;
}

.login_btn:hover {
  background: rgb(231, 169, 12);
}

.login_btn:disabled {
  opacity: 60%;
  cursor: default;
}

.login_btn:disabled:hover {
  background: rgba(251, 191, 36, 1);
}

.login__error {
  color: #c44;
  font-size: 12px;
  font-weight: 400;
  margin-top: 5px;
}
</style>
